/**
 * VRP System Performance Tracker
 *
 * Production-ready utility for measuring user journey timing with ZERO fallback values.
 * DEV-only code automatically tree-shaken in production builds.
 *
 * @module performance-tracker
 * @author VRP System v4
 */

export type NavigationSource =
  | 'user-click'
  | 'direct-navigation'
  | 'browser-nav'

export interface PerformanceStep {
  name: string
  timestamp: number // absolute DOMHighResTimeStamp
  elapsedMs: number // time since session start
  metadata?: {
    recordCount?: number
    cached?: boolean
    queryName?: string
    source?: string
  }
}

export interface PerformanceSession {
  tableType: string
  navigationSource: NavigationSource
  startTime: number // DOMHighResTimeStamp
  steps: PerformanceStep[]
  endTime?: number
  browserMetrics?: {
    dns?: number
    tcp?: number
    firstPaint?: number
  }
}

interface TimingReport {
  phases: Record<string, number>
  total: number
  bottleneck: string | null
  bottleneckPercentage: number
}

/**
 * Performance tracking class for VRP System user journeys
 * Singleton pattern with explicit error handling
 */
export class PerformanceTracker {
  private sessions: Map<string, PerformanceSession> = new Map()
  private currentTableType: string | null = null
  private readonly SESSION_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes

  /**
   * Initialize a new performance tracking session
   * @throws Error if tableType is invalid or empty
   */
  startTracking(tableType: string, source: NavigationSource): void {
    // Validate tableType
    if (
      !tableType ||
      typeof tableType !== 'string' ||
      tableType.trim() === ''
    ) {
      throw new Error('tableType must be a non-empty string')
    }

    if (!/^[a-zA-Z0-9\-]+$/.test(tableType)) {
      throw new Error(
        `Invalid tableType format: '${tableType}'. Only alphanumeric characters and hyphens allowed. ` +
          `Example: 'vehicles', 'vehicle-summary'`
      )
    }

    // Clean up old session if exists
    if (this.sessions.has(tableType)) {
      this.sessions.delete(tableType)
    }

    // Create new session
    const session: PerformanceSession = {
      tableType,
      navigationSource: source,
      startTime: performance.now(),
      steps: [],
    }

    this.sessions.set(tableType, session)
    this.currentTableType = tableType
  }

  /**
   * Record a timing point in the user journey
   * @throws Error if no active session or invalid step name
   */
  markStep(step: string, metadata?: PerformanceStep['metadata']): void {
    // Validate step name
    if (!step || typeof step !== 'string' || step.trim() === '') {
      throw new Error('step must be a non-empty string')
    }

    // Validate active session
    this.validateSession()

    const session = this.sessions.get(this.currentTableType!)
    if (!session) {
      throw new Error(
        `Cannot mark step '${step}': No active performance session. ` +
          `Did you forget to call perfDebug.startTracking()?`
      )
    }

    // Check for duplicate steps
    const existingStep = session.steps.find(s => s.name === step)
    if (existingStep) {
      throw new Error(
        `Cannot mark step '${step}': Step already recorded at ${existingStep.elapsedMs.toFixed(
          2
        )}ms. ` + `Each step should only be marked once per session.`
      )
    }

    const now = performance.now()
    const elapsedMs = now - session.startTime

    // Validate positive elapsed time
    if (elapsedMs < 0) {
      throw new Error(
        `Negative elapsed time detected: Step '${step}' recorded with ${elapsedMs.toFixed(
          2
        )}ms. ` +
          `This indicates a system clock issue or performance.now() inconsistency.`
      )
    }

    // Validate metadata if provided
    if (
      metadata !== undefined &&
      (typeof metadata !== 'object' || metadata === null)
    ) {
      throw new Error(
        `Invalid metadata type for step '${step}': Expected object, got ${typeof metadata}`
      )
    }

    const performanceStep: PerformanceStep = {
      name: step,
      timestamp: now,
      elapsedMs,
      metadata,
    }

    session.steps.push(performanceStep)
  }

  /**
   * Complete session and output formatted console report
   * @throws Error if no active session
   * @returns Timing report object for testing
   */
  endTracking(): TimingReport | null {
    this.validateSession()

    const session = this.sessions.get(this.currentTableType!)
    if (!session) {
      throw new Error('Cannot end tracking: No active performance session')
    }

    session.endTime = performance.now()
    session.browserMetrics = this.getBrowserMetrics()

    const report = this.calculatePhases(session)
    this.formatConsoleOutput(session, report)

    // Clean up session
    this.sessions.delete(this.currentTableType!)
    this.currentTableType = null

    return report
  }

  /**
   * Return raw timing data as object for testing/analysis
   * @throws Error if no active session
   */
  getTimings(): TimingReport | null {
    this.validateSession()

    const session = this.sessions.get(this.currentTableType!)
    if (!session) {
      throw new Error('Cannot get timings: No active performance session')
    }

    return this.calculatePhases(session)
  }

  /**
   * Validate that a session exists and is not stale
   * @throws Error if no session or session is stale
   */
  private validateSession(tableType?: string): void {
    const targetType = tableType || this.currentTableType

    if (!targetType) {
      throw new Error(
        'Cannot perform operation: No active performance session. ' +
          'Call perfDebug.startTracking(tableType, source) first.'
      )
    }

    const session = this.sessions.get(targetType)
    if (!session) {
      throw new Error(
        `Cannot perform operation: No performance session found for '${targetType}'. ` +
          'Call perfDebug.startTracking(tableType, source) first.'
      )
    }

    const sessionAge = performance.now() - session.startTime
    if (sessionAge > this.SESSION_TIMEOUT_MS) {
      throw new Error(
        `Performance session for '${targetType}' is stale (${(
          sessionAge / 1000
        ).toFixed(1)}s old). ` +
          'Maximum session age is 5 minutes. Start a new tracking session.'
      )
    }
  }

  /**
   * Collect browser Navigation Timing API metrics
   * Only returns meaningful positive values, no fallbacks
   */
  private getBrowserMetrics(): PerformanceSession['browserMetrics'] {
    if (!performance.getEntriesByType) {
      return undefined
    }

    const navigation = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming
    if (!navigation) {
      return undefined
    }

    const metrics: NonNullable<PerformanceSession['browserMetrics']> = {}

    const dns = navigation.domainLookupEnd - navigation.domainLookupStart
    if (dns > 0) {
      metrics.dns = dns
    }

    const tcp = navigation.connectEnd - navigation.connectStart
    if (tcp > 0) {
      metrics.tcp = tcp
    }

    const firstPaint =
      navigation.domContentLoadedEventEnd - navigation.domainLookupStart
    if (firstPaint > 0) {
      metrics.firstPaint = firstPaint
    }

    return Object.keys(metrics).length > 0 ? metrics : undefined
  }

  /**
   * Calculate phase durations and identify bottlenecks
   */
  private calculatePhases(session: PerformanceSession): TimingReport {
    const phases: Record<string, number> = {}
    const steps = session.steps

    // Validate we have steps to analyze
    if (steps.length === 0) {
      throw new Error(
        'Cannot calculate phases: Session has no performance steps. ' +
          'Call perfDebug.markStep() to record timing points before ending tracking.'
      )
    }

    // Calculate phase durations
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]
      const prevStep = i > 0 ? steps[i - 1] : null
      const duration = prevStep
        ? step.elapsedMs - prevStep.elapsedMs
        : step.elapsedMs

      phases[step.name] = duration
    }

    const total = session.endTime
      ? session.endTime - session.startTime
      : steps[steps.length - 1].elapsedMs

    // Identify primary bottleneck (phase >2000ms or largest contributor)
    let bottleneck: string | null = null
    let bottleneckPercentage = 0

    for (const [phase, duration] of Object.entries(phases)) {
      const percentage = (duration / total) * 100
      if (
        duration > 2000 ||
        bottleneck === null ||
        duration > phases[bottleneck]
      ) {
        bottleneck = phase
        bottleneckPercentage = percentage
      }
    }

    return { phases, total, bottleneck, bottleneckPercentage }
  }

  /**
   * Format and output console report
   */
  private formatConsoleOutput(
    session: PerformanceSession,
    report: TimingReport
  ): void {
    const lines: string[] = []
    lines.push(
      `[VRP Performance] ${session.tableType} table (${session.navigationSource}):\n`
    )

    // Group phases into categories
    const navPhases = Object.entries(report.phases).filter(([k]) =>
      k.startsWith('perf:')
    )
    const dataPhases = Object.entries(report.phases).filter(([k]) =>
      k.startsWith('data:')
    )

    // Navigation phases
    navPhases.forEach(([phase, duration]) => {
      const label = phase.replace('perf:', '').replace(/-/g, ' ')
      lines.push(`  ${label.padEnd(20)} ${duration.toFixed(0).padStart(6)}ms`)
    })

    if (dataPhases.length > 0) {
      lines.push('  ────────────────────────────')

      // Data phases with metadata
      dataPhases.forEach(([phase, duration]) => {
        const step = session.steps.find(s => s.name === phase)
        const label = phase.replace('data:', '')
        const warning = duration > 2000 ? ' ⚠️ SLOW' : ''
        const cached = step?.metadata?.cached ? ', cached' : ', network'
        const records = step?.metadata?.recordCount
          ? ` (${step.metadata.recordCount} records${cached})`
          : ''

        lines.push(
          `  Query: ${label.padEnd(15)} ${duration.toFixed(0).padStart(6)}ms${warning}${records}`
        )
      })

      lines.push('  ────────────────────────────')
    }

    lines.push(`\n  TOTAL: ${report.total.toFixed(0).padStart(18)}ms\n`)

    // Bottleneck analysis
    if (report.bottleneck) {
      const bottleneckStep = session.steps.find(
        s => s.name === report.bottleneck!
      )
      lines.push(
        `⚠️ PRIMARY BOTTLENECK: ${report.bottleneck} (${report.bottleneckPercentage.toFixed(
          0
        )}% of total time)`
      )

      if (bottleneckStep?.metadata?.recordCount) {
        lines.push(`   - ${bottleneckStep.metadata.recordCount} records loaded`)
      }
      if (bottleneckStep?.metadata?.cached === false) {
        lines.push('   - Network fetch (not cached)')
        lines.push(
          '   - Recommendation: Implement pagination or optimize backend query'
        )
      }
    }

    // Browser metrics if available
    if (session.browserMetrics) {
      lines.push('\n📊 Browser Metrics:')
      if (session.browserMetrics.dns) {
        lines.push(`   DNS Lookup: ${session.browserMetrics.dns.toFixed(0)}ms`)
      }
      if (session.browserMetrics.tcp) {
        lines.push(
          `   TCP Connection: ${session.browserMetrics.tcp.toFixed(0)}ms`
        )
      }
      if (session.browserMetrics.firstPaint) {
        lines.push(
          `   First Paint: ${session.browserMetrics.firstPaint.toFixed(0)}ms`
        )
      }
    }

    console.log(lines.join('\n'))
  }
}

// Singleton instance
export const perfDebug = new PerformanceTracker()
